import React from 'react';

import { useClassnames } from '@lumapps/classnames';
import { useDataAttributes } from '@lumapps/data-attributes';
import { ButtonProps } from '@lumapps/lumx/react';
import { ReactionState } from '@lumapps/reactions-core/api/types';
import { ReactionButton } from '@lumapps/reactions-core/components/ReactionButton';
import { REACTIONS_SCOPE } from '@lumapps/reactions-core/constants';
import { GLOBAL, useTranslate } from '@lumapps/translations';

import { useLikes } from '../../hook/useLikes';
import { REACTIONS_LIKES } from '../../keys';

import './index.scss';

export interface LikeButtonProps extends ReactionState, Omit<ButtonProps, 'children'> {
    showLabel?: boolean;
}

const CLASSNAME = 'like-reaction-button';

export const LikeButton = React.forwardRef<HTMLButtonElement, LikeButtonProps>((props, ref) => {
    const { reactionCounts, currentUserReactions, showLabel, className, ...forwardedProps } = props;

    const { defaultIcon, likedIcon, isLiked, hasThumbsUp } = useLikes({ reactionCounts, currentUserReactions });

    const { block } = useClassnames(CLASSNAME);
    const { get } = useDataAttributes(REACTIONS_SCOPE);

    const { translateAndReplace, translateKey } = useTranslate();

    return (
        <ReactionButton
            ref={ref}
            className={block(
                {
                    'is-liked': isLiked,
                    'has-thumbs-up': hasThumbsUp,
                },
                className,
            )}
            leftIcon={isLiked ? likedIcon : defaultIcon}
            aria-label={translateAndReplace(REACTIONS_LIKES.LIKE_BUTTON_ARIA, {
                NB: reactionCounts.total,
            })}
            aria-pressed={isLiked}
            {...get({ element: 'button', action: 'like' })}
            {...forwardedProps}
        >
            {String(reactionCounts.total)}{' '}
            {showLabel && translateKey(reactionCounts.total <= 1 ? GLOBAL.LIKE : GLOBAL.LIKES)}
        </ReactionButton>
    );
});
LikeButton.displayName = 'LikeButton';
